import { useCallback } from 'react';

import { TAppState } from '@/store/reducers/reducers';
import convertPathToRegex from '@/utils/tools/ConvertPathToRegex';
import { getHosts } from '@/utils/getHosts';
import { IEndpoint, IFeature, TFeatureSlugs } from '@/features/roles/store/models/features.model';
import { IAdvancedRole, roleSlugValues, TAdvancedRoleSlug } from '@/features/roles/store/models/advancedRole.model';
import { store } from '@/store/configuration';
import { getRoleFeatures } from '@/features/roles/store/reducers/accessControl.reducer';
import { EScope } from '@/features/roles/store/models/role.model';

import { useAppSelector } from './redux-helpers';

export function getAdvandeRoleFromScopes(
  scopes: string[] | undefined,
  config?: IAdvancedRole<IFeature>[],
): IAdvancedRole | undefined {
  const advancedRoles = config || store.getState()?.accessControl?.config?.value;

  const defaultRole = advancedRoles.find((advancedRole) => advancedRole.slug === roleSlugValues.READ_ONLY);
  if (!scopes) {
    return defaultRole;
  }
  return (
    advancedRoles?.find((configRole: IAdvancedRole) => {
      const configScopes: string[] = configRole.scopes.filter((scope) => scope !== EScope.KEYS);
      const isMatchingConfig = configScopes.every((scope) => scopes.includes(scope));
      const isMatchingScopes = scopes.every((scope) => (configRole.scopes as string[]).includes(scope));
      return isMatchingScopes && isMatchingConfig && configRole.slug !== roleSlugValues.CONFIG;
    }) || defaultRole
  );
}

export async function canCallEndpoint(
  endpoint: IEndpoint,
  userRole: TAdvancedRoleSlug | undefined,
  allRoles?: IAdvancedRole<IFeature>[],
): Promise<{ hasAccess: boolean; error?: string }> {
  try {
    const state: TAppState = store.getState();
    const hosts = getHosts(state.dashboardConfig.config);

    if (!userRole) {
      userRole = state.accessControl?.role.value?.slug;
    }

    if (state.accessControl?.config?.value) {
      if (!userRole) {
        return { hasAccess: false, error: `A role is needed to access : '${endpoint.method} | '${endpoint.url}'` };
      }

      const roles = allRoles || (state.accessControl.config.value as IAdvancedRole<IFeature>[]);

      const role = roles.find((r) => r.slug === userRole);
      if (!role) {
        return { hasAccess: false, error: `Role '${userRole}' does not exists` };
      }

      const path = hosts.reduce((prev, current) => {
        return prev.replace(current, '');
      }, endpoint.url);

      const hasAccess = role.features
        .reduce<IEndpoint[]>((prev, current) => [...prev, ...current.endpoints], [])
        .some((allowedEndpoint) => {
          return (
            convertPathToRegex(allowedEndpoint.url.replace(/\/$/, '')).test(path.replace(/\/$/, '')) &&
            (allowedEndpoint.method === endpoint.method || allowedEndpoint.method === 'ALL')
          );
        });

      return {
        hasAccess,
        error: hasAccess
          ? undefined
          : `Role '${userRole}' does not have access to ${endpoint.method} | '${endpoint.url}'`,
      };
    }
    return { hasAccess: false, error: 'Roles configuration not found' };
  } catch (error) {
    return {
      hasAccess: false,
      error: error.message,
    };
  }
}

export function useAccesControl() {
  const role = useAppSelector(
    (state) => state.accessControl.role && !state.accessControl.role.isLoading && state.accessControl.role.value,
  );
  const config = useAppSelector((state) => state.accessControl.config.value).filter(
    (role) => role.slug !== roleSlugValues.CONFIG,
  );
  const features = getRoleFeatures(role, config);

  const hasAccessToFeature = (
    featureSlug: string | string[],
    mode: 'AND' | 'OR' = 'AND',
    strict: boolean = false,
  ): featureSlug is TFeatureSlugs => {
    const featuresSlugs = features.map((feature) => feature.slug);
    if (featuresSlugs.includes('ALL_APP') && !strict) {
      return true;
    }
    if (Array.isArray(featureSlug)) {
      return mode === 'AND'
        ? featureSlug.every((slug) => featuresSlugs.includes(slug as TFeatureSlugs))
        : featureSlug.some((slug) => featuresSlugs.includes(slug as TFeatureSlugs));
    } else {
      return featuresSlugs.includes(featureSlug as TFeatureSlugs);
    }
  };

  const hasAccessToRoute = useCallback(
    (route: string): boolean =>
      features
        .reduce<string[]>((prev, current) => {
          return [...prev, ...(current.routes || [])];
        }, [])
        .some((allowedRoute) => convertPathToRegex(allowedRoute).test(route)),
    [features],
  );

  const getFeatureFromRoute = useCallback(
    (route: string) =>
      features.find((feature) => {
        return feature.routes.includes(route);
      }),
    [features],
  );

  const hasAccessToEndpoint = useCallback(
    (endPoint: IEndpoint) => !!role && canCallEndpoint(endPoint, role.slug, config),
    [config, role],
  );

  const advandeRoleFromScopes = useCallback(
    (scopes: string[] | undefined): IAdvancedRole => {
      return getAdvandeRoleFromScopes(scopes, config);
    },
    [config],
  );

  const advandeRoleFromSlug = useCallback(
    (roleSlug: string): IAdvancedRole<IFeature> => {
      const defaultRole = config.find((role) => role.slug === roleSlugValues.READ_ONLY);
      return config.find((role) => roleSlug === role.slug) || defaultRole;
    },
    [config],
  );

  return {
    hasAccessToFeature,
    hasAccessToRoute,
    hasAccessToEndpoint,
    getFeatureFromRoute,
    advandeRoleFromScopes,
    advandeRoleFromSlug,
    role,
    features: features.map((feature) => feature.slug),
    routes: features.flatMap((current) => current.routes || []),
    endPoints: features.flatMap((current) => current.endpoints || []),
    scopes: role && role.scopes,
    config,
  };
}
